import { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import store, { AllState } from '../../../../../../../../../../store'
import { requireSave } from '../../../../../../../../../../store/requiredSaveData'
import {
  getWbsItemGanttColors,
  saveWbsItemGanttColors,
  WbsItemGanttColorResponse,
} from '../../../../../../../../../../infrastructure/api/wbs-item-gantt-colors'
import { GanttBarColorName, isGanttBarColorName } from '../../ganttUtil'

export type WbsItemGanttColorMap = ReadonlyMap<string, GanttBarColorName>

export const toColorMap = (
  settings: WbsItemGanttColorResponse[]
): Map<string, GanttBarColorName> => {
  const colorMap = new Map<string, GanttBarColorName>()
  settings.forEach(setting => {
    const { color, wbsItemUuid } = setting
    if (color && isGanttBarColorName(color)) {
      colorMap.set(wbsItemUuid, color)
    }
  })
  return colorMap
}

export const useWbsItemGanttColorData = () => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  // 画面に表示する色。編集直後は未保存の値を含む
  const [colors, setColors] = useState<WbsItemGanttColorMap>(new Map())
  // 保存・未保存判定は保存ボタンのハンドラ（依存配列にこのフックを持たない）から
  // 呼ばれるため、state ではなく ref を読んで最新値を保証する
  const colorsRef = useRef<WbsItemGanttColorMap>(colors)
  const savedColorsRef = useRef<Map<string, GanttBarColorName>>(new Map())

  const applyColors = useCallback((next: WbsItemGanttColorMap) => {
    colorsRef.current = next
    setColors(next)
  }, [])

  const fetchColors = useCallback(async () => {
    if (!projectUuid) return
    const response = await getWbsItemGanttColors()
    if (response.hasError) return
    const fetched = toColorMap(response.json ?? [])
    savedColorsRef.current = fetched
    applyColors(new Map(fetched))
  }, [projectUuid, applyColors])

  // 他の WBS 項目と同じく、保存ボタンを押すまでサーバーへ送らない
  const changeColor = useCallback(
    (wbsItemUuid: string, color: GanttBarColorName) => {
      const next = new Map(colorsRef.current)
      next.set(wbsItemUuid, color)
      applyColors(next)
      if (savedColorsRef.current.get(wbsItemUuid) !== color) {
        store.dispatch(requireSave())
      }
    },
    [applyColors]
  )

  const hasUnsavedChanges = useCallback((): boolean => {
    const saved = savedColorsRef.current
    const current = colorsRef.current
    if (saved.size !== current.size) return true
    for (const [wbsItemUuid, color] of current) {
      if (saved.get(wbsItemUuid) !== color) return true
    }
    return false
  }, [])

  const saveColors = useCallback(async () => {
    if (!projectUuid) return
    const saved = savedColorsRef.current
    const current = colorsRef.current
    const edited = Array.from(current)
      .filter(([wbsItemUuid, color]) => saved.get(wbsItemUuid) !== color)
      .map(([wbsItemUuid, color]) => ({ wbsItemUuid, color }))
    if (!edited.length) return

    const response = await saveWbsItemGanttColors({ edited })
    if (response.hasError) return
    savedColorsRef.current = new Map(current)
  }, [projectUuid])

  return { colors, fetchColors, changeColor, saveColors, hasUnsavedChanges }
}
